import { COMMAND_ARITY, COMMAND_NAME_SET, isCommandError, type CommandHandler } from './types';
import { handlers } from './commands';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function applyAliases(method: string, obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  const idLike = out.labelOrId ?? out.id ?? out.label;

  switch (method) {
    case 'findNode':
    case 'updateNode':
    case 'removeNode':
    case 'removeParticipant':
    case 'removeMessage':
    case 'dissolveGroup':
    case 'switchPage':
    case 'getPageDetail':
    case 'setPageDescription':
      if (out.labelOrId == null && idLike != null) out.labelOrId = String(idLike);
      break;
    case 'removeFragment':
    case 'updateFragment':
    case 'updateEdge':
    case 'updateMessage':
      if (out.id == null && typeof out.labelOrId === 'string') out.id = out.labelOrId;
      break;
    case 'importFromMermaid':
      if (out.mmd == null && typeof out.mermaid === 'string') out.mmd = out.mermaid;
      break;
    case 'setDiagramType':
      if (out.type == null && typeof out.diagramType === 'string') out.type = out.diagramType;
      break;
    case 'clearDiagram':
      if (out.diagramType == null && typeof out.type === 'string') out.diagramType = out.type;
      break;
    case 'frameNodes':
    case 'createGroup':
      if (out.labelOrIds == null) {
        out.labelOrIds = out.labels ?? out.nodes ?? out.ids;
      }
      break;
    case 'linkNodeToPage':
      if (out.nodeLabelOrId == null) out.nodeLabelOrId = out.node ?? out.labelOrId;
      if (out.pageNameOrId == null) out.pageNameOrId = out.page ?? out.pageId;
      break;
    case 'updateNodeMeta':
      if (out.labelOrId == null && idLike != null) out.labelOrId = String(idLike);
      break;
    case 'updateCatalog':
    case 'placeCatalogInstance':
      if (out.catalogId == null && idLike != null) out.catalogId = String(idLike);
      if (out.labelOrId == null && idLike != null) out.labelOrId = String(idLike);
      break;
    case 'createChildView':
      if (out.nodeLabelOrId == null) out.nodeLabelOrId = out.node ?? out.labelOrId ?? out.id;
      break;
    default:
      break;
  }
  return out;
}

/**
 * Normalize any call shape (object, scalar, or positional array) into a
 * single params object. This is what lets in-app AI, MCP, and applyBatch
 * share one handler table.
 */
export function normalizeParams(method: string, raw: unknown): Record<string, unknown> {
  if (raw == null) return {};

  const spec = COMMAND_ARITY[method];

  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    const key = spec?.scalarKey ?? spec?.positional[0];
    return key ? { [key]: raw } : {};
  }

  if (Array.isArray(raw)) {
    if (raw.length === 0) return {};
    if (!spec || spec.positional.length === 0) {
      return isPlainObject(raw[0]) ? applyAliases(method, raw[0]) : {};
    }

    const out: Record<string, unknown> = {};
    spec.positional.forEach((key, index) => {
      if (index >= raw.length) return;
      if (spec.spreadLast && index === spec.positional.length - 1 && isPlainObject(raw[index])) {
        Object.assign(out, raw[index]);
      } else {
        out[key] = raw[index];
      }
    });
    return applyAliases(method, out);
  }

  if (isPlainObject(raw)) {
    return applyAliases(method, raw);
  }

  return {};
}

export function invoke(method: string, raw?: unknown): unknown {
  if (!COMMAND_NAME_SET.has(method)) {
    return { error: `Unknown method: ${method}` };
  }
  const handler = (handlers as Record<string, CommandHandler | undefined>)[method];
  if (!handler) {
    return { error: `Method "${method}" is not registered` };
  }
  return handler(normalizeParams(method, raw));
}

export function applyBatch(
  operations: Array<{ method: string; params?: unknown }>,
): Array<{ result?: unknown; error?: string }> {
  return operations.map(op => {
    try {
      if (!op?.method) return { error: 'Missing method name' };
      const result = invoke(op.method, op.params);
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        return { error: `Method "${op.method}" is async — use individual calls for async operations` };
      }
      if (isCommandError(result)) return { error: result.error };
      return { result };
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
  });
}
